import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';

import axios from 'axios';

import useSnackbar from 'hooks/useSnackbar';

import { sleep } from 'utils/common';

const AuthContext = createContext(null);

const SESSION_REPLACED_CODE = 'SESSION_REPLACED';
const SESSION_REVOKED_CODE = 'SESSION_REVOKED';
const SESSION_EXPIRED_CODE = 'SESSION_EXPIRED';

const SESSION_MESSAGE_BY_CODE = {
  [SESSION_REPLACED_CODE]:
    "You've been signed out. Your account is now active on another device.",
  [SESSION_REVOKED_CODE]:
    'Your session is no longer valid. Please log in again.',
  [SESSION_EXPIRED_CODE]:
    'Your session expired. Please log in again.'
};

function clearStoredAuth() {
  localStorage.removeItem('isAuthenticated');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
}

function getSessionMessage(code, fallbackMessage) {
  return SESSION_MESSAGE_BY_CODE[code] || fallbackMessage || 'Please log in again.';
}

export const AuthProvider = ({ children }) => {
  const { errorMessage, successMessage } = useSnackbar();
  const forceLogoutInProgressRef = useRef(false);
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => localStorage.getItem('isAuthenticated') === 'Y'
  );
  const [isAuthReady, setIsAuthReady] = useState(false);

  const axiosServices = useMemo(
    () =>
      axios.create({
        baseURL: '/api/v1',
        headers: {
          'Content-Type': 'application/json'
        }
      }),
    []
  );

  const setAxiosAuthHeader = useCallback(
    (accessToken) => {
      if (accessToken) {
        axiosServices.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        return;
      }

      delete axiosServices.defaults.headers.common.Authorization;
    },
    [axiosServices]
  );

  const forceLogout = useCallback(
    async ({ message, variant = 'error' } = {}) => {
      if (forceLogoutInProgressRef.current) {
        return { status: true };
      }

      forceLogoutInProgressRef.current = true;

      try {
        setIsAuthenticated(false);
        setAxiosAuthHeader(null);
        clearStoredAuth();

        if (message) {
          if (variant === 'success') {
            successMessage(message);
          } else {
            errorMessage(message);
          }
        }

        return { status: true };
      } catch (error) {
        console.error('Force logout error:', error);
        return { status: false, message: error.message };
      } finally {
        forceLogoutInProgressRef.current = false;
      }
    },
    [errorMessage, setAxiosAuthHeader, successMessage]
  );

  const refreshAccessToken = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        return { status: false, message: 'No refresh token available' };
      }

      const response = await axiosServices.post(
        '/refresh-token',
        { refreshToken },
        {
          skipAuthRefresh: true,
          skipForceLogout: true
        }
      );

      if (response.data.authentication_success) {
        localStorage.setItem('accessToken', response.data.accessToken);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setAxiosAuthHeader(response.data.accessToken);
        return { status: true, message: 'Token refreshed successfully' };
      }

      return {
        status: false,
        message: response.data.message || 'Token refresh failed',
        code: response.data.code
      };
    } catch (error) {
      console.error('Token refresh error:', error);
      return {
        status: false,
        code: error.response?.data?.code,
        message:
          error.response?.data?.message ||
          getSessionMessage(error.response?.data?.code, 'Token refresh failed')
      };
    }
  }, [axiosServices, setAxiosAuthHeader]);

  useEffect(() => {
    setAxiosAuthHeader(localStorage.getItem('accessToken'));
  }, [setAxiosAuthHeader]);

  useEffect(() => {
    const requestInterceptor = axiosServices.interceptors.request.use((config) => {
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return config;
    });

    const responseInterceptor = axiosServices.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config || {};
        const response = error.response;
        const responseCode = response?.data?.code;
        const responseMessage = response?.data?.message;

        if (
          response?.status === 401 &&
          responseCode &&
          Object.prototype.hasOwnProperty.call(SESSION_MESSAGE_BY_CODE, responseCode)
        ) {
          if (!originalRequest.skipForceLogout) {
            await forceLogout({
              message: getSessionMessage(responseCode, responseMessage)
            });
          }
          return Promise.reject(error);
        }

        if (response?.status === 403) {
          if (!originalRequest.skipForceLogout) {
            await forceLogout({
              message: responseMessage || 'Access denied.'
            });
          }
          return Promise.reject(error);
        }

        if (
          response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.skipAuthRefresh &&
          !String(originalRequest.url || '').endsWith('/refresh-token')
        ) {
          originalRequest._retry = true;

          try {
            const refreshResult = await refreshAccessToken();
            if (refreshResult.status) {
              localStorage.setItem('isAuthenticated', 'Y');
              originalRequest.headers = {
                ...(originalRequest.headers || {}),
                Authorization: `Bearer ${localStorage.getItem('accessToken')}`
              };
              return axiosServices(originalRequest);
            }

            if (!originalRequest.skipForceLogout) {
              await forceLogout({
                message: getSessionMessage(refreshResult.code, refreshResult.message)
              });
            }

            return Promise.reject(new Error('Token refresh failed'));
          } catch (refreshError) {
            if (!originalRequest.skipForceLogout) {
              await forceLogout({
                message: getSessionMessage(
                  refreshError.response?.data?.code,
                  refreshError.response?.data?.message || refreshError.message
                )
              });
            }
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axiosServices.interceptors.request.eject(requestInterceptor);
      axiosServices.interceptors.response.eject(responseInterceptor);
    };
  }, [axiosServices, forceLogout, refreshAccessToken]);

  useEffect(() => {
    let cancelled = false;

    const validateStoredSession = async () => {
      const hasStoredSession = localStorage.getItem('isAuthenticated') === 'Y';
      const refreshToken = localStorage.getItem('refreshToken');

      if (!hasStoredSession) {
        if (!cancelled) {
          setIsAuthenticated(false);
          setIsAuthReady(true);
        }
        return;
      }

      if (!refreshToken) {
        await forceLogout({
          message: getSessionMessage(
            SESSION_EXPIRED_CODE,
            'Your session expired. Please log in again.'
          )
        });

        if (!cancelled) {
          setIsAuthReady(true);
        }
        return;
      }

      try {
        const refreshResult = await refreshAccessToken();

        if (cancelled) {
          return;
        }

        if (refreshResult.status) {
          localStorage.setItem('isAuthenticated', 'Y');
          setIsAuthenticated(true);
          return;
        }

        await forceLogout({
          message: getSessionMessage(refreshResult.code, refreshResult.message)
        });
      } finally {
        if (!cancelled) {
          setIsAuthReady(true);
        }
      }
    };

    void validateStoredSession();

    return () => {
      cancelled = true;
    };
  }, [forceLogout, refreshAccessToken]);

  const login = useCallback(
    async (values) => {
      try {
        const response = await axiosServices.post(
          '/login',
          {
            ...values,
            client_type: 'web'
          },
          {
            skipAuthRefresh: true,
            skipForceLogout: true
          }
        );

        if (response.data.authentication_success) {
          localStorage.setItem('accessToken', response.data.accessToken);
          localStorage.setItem('refreshToken', response.data.refreshToken);
          localStorage.setItem('isAuthenticated', 'Y');
          localStorage.setItem('user', JSON.stringify(response.data.user));
          setAxiosAuthHeader(response.data.accessToken);
          setIsAuthenticated(true);
          return { status: true, message: 'Login successful' };
        }

        return {
          status: false,
          message: response.data.message || 'Login failed',
          code: response.data.code,
          membershipSite: response.data.membership_site
        };
      } catch (error) {
        console.error('Login error:', error);
        return {
          status: false,
          code: error.response?.data?.code,
          // The expired-membership 403 names the matching site so the login UI
          // can render a "Renew it on your membership site" link.
          membershipSite: error.response?.data?.membership_site,
          message:
            error.response?.data?.message ||
            getSessionMessage(error.response?.data?.code, 'Authentication failed')
        };
      }
    },
    [axiosServices, setAxiosAuthHeader]
  );

  const logout = useCallback(async () => {
    try {
      await axiosServices.post(
        '/logout',
        {},
        {
          skipAuthRefresh: true,
          skipForceLogout: true
        }
      );
    } catch (error) {
      console.error('Logout request error:', error);
    }

    await forceLogout({
      message: 'Successfully logged out',
      variant: 'success'
    });
  }, [axiosServices, forceLogout]);

  const signup = async (values) => {
    console.log(values);
    await sleep(1000);
    return { status: true, data: '' };
  };

  // Triggers the matching membership site's native WordPress password-reset email
  // via the bridge endpoint. Neutral by design: the server always responds 200
  // with a non-committal message (no account enumeration). `skipForceLogout`
  // keeps the (unauthenticated) request out of the 401/403 auto-logout path.
  const forgotPassword = async ({ email } = {}) => {
    try {
      const response = await axiosServices.post(
        '/forgot_password',
        { log: email },
        { skipAuthRefresh: true, skipForceLogout: true }
      );
      return { status: true, message: response.data?.message || '' };
    } catch (error) {
      return {
        status: false,
        message:
          error.response?.data?.message || error.message || 'Something went wrong. Please try again.'
      };
    }
  };

  const resetPassword = async (values) => {
    console.log(values);
    await sleep(1000);
    return { status: true, data: '' };
  };

  return (
    <AuthContext.Provider
      value={{
        axiosServices,
        login,
        signup,
        forgotPassword,
        resetPassword,
        logout,
        forceLogout,
        isAuthenticated,
        isAuthReady
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node
};

export default AuthContext;
